import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Send, ArrowLeft } from 'lucide-react'
import MainLayout from '@/components/Layout/MainLayout'
import { Button } from '@/components/ui/Button'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { SimpleMessageComposer } from '@/components/inbox/SimpleMessageComposer'
import { useRouteParams } from '@/hooks/useRoutes'
import { useFileUpload } from '@/hooks/useFileUpload'
import routes from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'

interface Inbox {
  id: string
  slug: string
  name: string
}

interface Visitor {
  id: string
  name: string
  email: string | null
  identified: boolean
  last_seen_at: string | null
  country_code: string | null
}

interface NewConversationProps {
  current_user: CurrentUser
  inbox: Inbox
  visitor: Visitor
}

export default function NewConversation({
  current_user,
  inbox,
  visitor
}: NewConversationProps) {
  const { withAccount, withInbox } = useRouteParams()
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const fileUpload = useFileUpload()

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if ((!message.trim() && !fileUpload.pendingFile) || isSending) return

    // Upload file if pending
    let attachmentSignedId: string | null = null
    if (fileUpload.pendingFile && fileUpload.pendingFile.status !== 'error') {
      attachmentSignedId = await fileUpload.uploadFile()
      if (!attachmentSignedId && fileUpload.pendingFile) {
        return
      }
    }

    setIsSending(true)
    router.post(routes.conversations.create.path(withInbox()), {
      content: message,
      visitor_id: visitor.id,
      ...(attachmentSignedId && { attachment_signed_id: attachmentSignedId })
    }, {
      onFinish: () => {
        setIsSending(false)
        fileUpload.clearFile()
      }
    })
  }

  return (
    <>
      <Head title={`New conversation with ${visitor.name}`} />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: inbox.name, href: routes.inbox.show.path(withAccount()) },
          { label: 'New Conversation' }
        ]}
      >
        <div className="flex flex-1 flex-col min-h-0">
          {/* Header */}
          <div className="flex items-center gap-4 p-4 border-b border-border">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.visit(routes.visitors.show.path(withAccount()))}
              className="cursor-pointer"
            >
              <ArrowLeft className="size-4" />
            </Button>

            <VisitorAvatar
              name={visitor.name}
              identified={visitor.identified}
              active={false}
              live={false}
              countryCode={visitor.country_code}
              size="md"
            />

            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">
                New conversation with {visitor.name}
              </h2>
              {visitor.email && (
                <p className="text-sm text-muted-foreground truncate">{visitor.email}</p>
              )}
            </div>
          </div>

          {/* Empty message area with prompt */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Send className="size-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Start a conversation</h3>
              <p className="text-muted-foreground text-sm">
                Send a message to {visitor.name} to start a new conversation.
                The conversation will be created when you send your first message.
              </p>
            </div>
          </div>

          {/* Message composer */}
          <SimpleMessageComposer
            value={message}
            onChange={setMessage}
            onSubmit={handleSend}
            isSending={isSending}
            fileUpload={fileUpload}
            autoFocus
          />
        </div>
      </MainLayout>
    </>
  )
}
